import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { Save, ArrowCounterclockwise, Clock, ExclamationTriangle } from 'react-bootstrap-icons';
import PageLayout from '../../layouts/PageLayout';
import { configurationAPI } from '../../services';
import { toast } from 'react-toastify';

// Config keys for booking gate
const BOOKING_KEYS = {
  enabled: 'booking.confirmation_gate.enabled',
  threshold_percent: 'booking.confirmation_gate.threshold_percent',
  staff_timeout_seconds: 'booking.confirmation_gate.staff_timeout_seconds',
  lookahead_minutes: 'booking.confirmation_gate.lookahead_minutes',
  timeout_penalty_points: 'booking.confirmation_gate.timeout_penalty_points',
};

// Config key for appeal escalation (JSON)
const APPEAL_KEY = 'appeal_escalation';

const DEFAULTS = {
  booking: {
    enabled: true,
    threshold_percent: 80,
    staff_timeout_minutes: 3, // stored as seconds (×60)
    lookahead_minutes: 120,
    timeout_penalty_points: 1,
  },
  appeal: {
    remind_hours: 48,
    escalate_hours: 72,
    auto_approve_hours: 168,
  },
};

async function fetchConfigByKey(key) {
  try {
    const res = await configurationAPI.getConfigurationByKey(key);
    return res?.data || res || null;
  } catch {
    return null;
  }
}

export default function AutoApproveSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Raw config objects (with id) for update calls
  const [configRefs, setConfigRefs] = useState({});

  // Booking gate form state
  const [booking, setBooking] = useState({ ...DEFAULTS.booking });
  // Appeal escalation form state
  const [appeal, setAppeal] = useState({ ...DEFAULTS.appeal });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [enabled, threshold, timeout, lookahead, penalty, appealCfg] = await Promise.all([
        fetchConfigByKey(BOOKING_KEYS.enabled),
        fetchConfigByKey(BOOKING_KEYS.threshold_percent),
        fetchConfigByKey(BOOKING_KEYS.staff_timeout_seconds),
        fetchConfigByKey(BOOKING_KEYS.lookahead_minutes),
        fetchConfigByKey(BOOKING_KEYS.timeout_penalty_points),
        fetchConfigByKey(APPEAL_KEY),
      ]);

      setConfigRefs({
        enabled,
        threshold,
        timeout,
        lookahead,
        penalty,
        appeal: appealCfg,
      });

      setBooking({
        enabled: enabled ? enabled.value === 'true' : DEFAULTS.booking.enabled,
        threshold_percent: threshold ? Number(threshold.value) : DEFAULTS.booking.threshold_percent,
        staff_timeout_minutes: timeout
          ? Math.round(Number(timeout.value) / 60)
          : DEFAULTS.booking.staff_timeout_minutes,
        lookahead_minutes: lookahead
          ? Number(lookahead.value)
          : DEFAULTS.booking.lookahead_minutes,
        timeout_penalty_points: penalty
          ? Number(penalty.value)
          : DEFAULTS.booking.timeout_penalty_points,
      });

      if (appealCfg) {
        try {
          const parsed = JSON.parse(appealCfg.value);
          setAppeal({
            remind_hours: parsed.remind_hours ?? DEFAULTS.appeal.remind_hours,
            escalate_hours: parsed.escalate_hours ?? DEFAULTS.appeal.escalate_hours,
            auto_approve_hours: parsed.auto_approve_hours ?? DEFAULTS.appeal.auto_approve_hours,
          });
        } catch {
          setAppeal({ ...DEFAULTS.appeal });
        }
      }
    } catch (err) {
      toast.error('Không thể tải cấu hình');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveConfig = async (ref, value, createPayload) => {
    if (ref?.id) {
      return configurationAPI.updateConfiguration(ref.id, { value: String(value) });
    }
    return configurationAPI.createConfiguration({ ...createPayload, value: String(value) });
  };

  const handleSaveBooking = async () => {
    setSaving(true);
    try {
      await Promise.all([
        saveConfig(configRefs.enabled, booking.enabled, {
          key: BOOKING_KEYS.enabled,
          type: 'BOOLEAN',
          category: 'ATTENDANCE',
          description: 'Bật/tắt cổng xác nhận đặt bàn qua Telegram',
        }),
        saveConfig(configRefs.threshold, booking.threshold_percent, {
          key: BOOKING_KEYS.threshold_percent,
          type: 'NUMBER',
          category: 'ATTENDANCE',
          description: 'Ngưỡng công suất (%) cần xác nhận staff',
        }),
        saveConfig(configRefs.timeout, booking.staff_timeout_minutes * 60, {
          key: BOOKING_KEYS.staff_timeout_seconds,
          type: 'NUMBER',
          category: 'ATTENDANCE',
          description: 'Thời gian chờ staff phản hồi (giây) trước khi tự động duyệt',
        }),
        saveConfig(configRefs.lookahead, booking.lookahead_minutes, {
          key: BOOKING_KEYS.lookahead_minutes,
          type: 'NUMBER',
          category: 'ATTENDANCE',
          description: 'Khoảng thời gian nhìn trước (phút) để kích hoạt cổng',
        }),
        saveConfig(configRefs.penalty, booking.timeout_penalty_points, {
          key: BOOKING_KEYS.timeout_penalty_points,
          type: 'NUMBER',
          category: 'ATTENDANCE',
          description: 'Điểm phạt khi staff không phản hồi trong thời gian quy định',
        }),
      ]);
      toast.success('Đã lưu cấu hình cổng đặt bàn');
      loadAll();
    } catch (err) {
      toast.error(err?.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAppeal = async () => {
    setSaving(true);
    try {
      const appealValue = JSON.stringify({
        remind_hours: Number(appeal.remind_hours),
        escalate_hours: Number(appeal.escalate_hours),
        auto_approve_hours: Number(appeal.auto_approve_hours),
      });
      await saveConfig(configRefs.appeal, appealValue, {
        key: APPEAL_KEY,
        type: 'JSON',
        category: 'POINT',
        description: 'Cấu hình leo thang và tự động duyệt giải trình điểm phạt',
      });
      toast.success('Đã lưu cấu hình giải trình điểm phạt');
      loadAll();
    } catch (err) {
      toast.error(err?.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  };

  const setBookingField = (field, value) =>
    setBooking((prev) => ({ ...prev, [field]: value }));

  const setAppealField = (field, value) =>
    setAppeal((prev) => ({ ...prev, [field]: value }));

  return (
    <PageLayout>
      <Row>
        <Col xl={12} className="mb-4">
          <div className="d-flex align-items-center gap-2">
            <Clock size={22} className="text-primary" />
            <h3 className="mb-0">Cài đặt tự động duyệt</h3>
          </div>
          <p className="text-muted mt-1 mb-0">
            Quản lý các ngưỡng thời gian và điều kiện để hệ thống tự động duyệt hoặc leo thang
          </p>
        </Col>

        {loading ? (
          <Col xl={12} className="text-center py-5">
            <Spinner animation="border" />
          </Col>
        ) : (
          <>
            {/* BOOKING GATE */}
            <Col xl={12} className="mb-4">
              <Card>
                <Card.Header className="d-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="mb-0">Cổng xác nhận đặt bàn</h5>
                    <small className="text-muted">
                      Khi công suất vượt ngưỡng, yêu cầu đặt bàn sẽ chờ staff xác nhận qua Telegram
                    </small>
                  </div>
                  <Badge bg={booking.enabled ? 'success' : 'secondary'}>
                    {booking.enabled ? 'Đang bật' : 'Đã tắt'}
                  </Badge>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Check
                          type="switch"
                          id="booking-enabled"
                          label="Bật cổng xác nhận đặt bàn"
                          checked={booking.enabled}
                          onChange={(e) => setBookingField('enabled', e.target.checked)}
                        />
                        <Form.Text className="text-muted">
                          Tắt để tất cả đặt bàn được duyệt tự động mà không cần staff xác nhận
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Ngưỡng công suất <span className="text-muted">(%)</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          value={booking.threshold_percent}
                          onChange={(e) =>
                            setBookingField('threshold_percent', Number(e.target.value))
                          }
                        />
                        <Form.Text className="text-muted">
                          Khi quán đạt ≥ X% công suất thì kích hoạt cổng (mặc định: 80%)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Thời gian chờ staff <span className="text-muted">(phút)</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          max={60}
                          value={booking.staff_timeout_minutes}
                          onChange={(e) =>
                            setBookingField('staff_timeout_minutes', Number(e.target.value))
                          }
                        />
                        <Form.Text className="text-muted">
                          Sau X phút không có phản hồi → tự động duyệt (mặc định: 3 phút)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Nhìn trước <span className="text-muted">(phút)</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={15}
                          max={480}
                          value={booking.lookahead_minutes}
                          onChange={(e) =>
                            setBookingField('lookahead_minutes', Number(e.target.value))
                          }
                        />
                        <Form.Text className="text-muted">
                          Chỉ kích hoạt cổng cho đặt bàn trong X phút tới (mặc định: 120 phút)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Điểm phạt khi timeout <span className="text-muted">(điểm)</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          max={10}
                          value={booking.timeout_penalty_points}
                          onChange={(e) =>
                            setBookingField('timeout_penalty_points', Number(e.target.value))
                          }
                        />
                        <Form.Text className="text-muted">
                          Điểm trừ khi staff không trả lời trong thời gian quy định (mặc định: 1 điểm)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Alert variant="info" className="mb-0 py-2">
                        <small>
                          <strong>Luồng:</strong> Đặt bàn → quán ≥ {booking.threshold_percent}%
                          công suất trong {booking.lookahead_minutes} phút tới → Telegram gửi yêu cầu
                          xác nhận → staff không trả lời trong {booking.staff_timeout_minutes} phút →
                          tự động duyệt + trừ {booking.timeout_penalty_points} điểm
                        </small>
                      </Alert>
                    </Col>
                  </Row>
                </Card.Body>
                <Card.Footer className="d-flex justify-content-end gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setBooking({ ...DEFAULTS.booking })}
                    disabled={saving}
                  >
                    <ArrowCounterclockwise size={14} className="me-1" />
                    Mặc định
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveBooking}
                    disabled={saving}
                  >
                    {saving ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <Save size={14} className="me-1" />
                    )}
                    Lưu cổng đặt bàn
                  </Button>
                </Card.Footer>
              </Card>
            </Col>

            {/* APPEAL ESCALATION */}
            <Col xl={12} className="mb-4">
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Giải trình điểm phạt</h5>
                  <small className="text-muted">
                    Khi nhân viên giải trình điểm phạt, hệ thống sẽ tự động leo thang và duyệt nếu
                    quản lý không xử lý đúng hạn
                  </small>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Nhắc nhở lần 1 <span className="text-muted">(giờ)</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          max={appeal.escalate_hours - 1}
                          value={appeal.remind_hours}
                          onChange={(e) => setAppealField('remind_hours', Number(e.target.value))}
                        />
                        <Form.Text className="text-muted">
                          Gửi Telegram nhắc manager sau X giờ pending (mặc định: 48 giờ)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Leo thang lên Admin <span className="text-muted">(giờ)</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={appeal.remind_hours + 1}
                          max={appeal.auto_approve_hours - 1}
                          value={appeal.escalate_hours}
                          onChange={(e) => setAppealField('escalate_hours', Number(e.target.value))}
                        />
                        <Form.Text className="text-muted">
                          Thông báo admin sau X giờ nếu manager vẫn chưa xử lý (mặc định: 72 giờ)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Tự động miễn phạt <span className="text-muted">(giờ)</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={appeal.escalate_hours + 1}
                          value={appeal.auto_approve_hours}
                          onChange={(e) =>
                            setAppealField('auto_approve_hours', Number(e.target.value))
                          }
                        />
                        <Form.Text className="text-muted">
                          Tự động duyệt (miễn phạt) nếu sau X giờ vẫn pending (mặc định: 168 giờ = 7 ngày)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      {appeal.remind_hours >= appeal.escalate_hours && (
                        <Alert variant="warning" className="py-2 mb-2">
                          <ExclamationTriangle size={14} className="me-1" />
                          <small>
                            Giờ nhắc nhở phải nhỏ hơn giờ leo thang
                          </small>
                        </Alert>
                      )}
                      {appeal.escalate_hours >= appeal.auto_approve_hours && (
                        <Alert variant="warning" className="py-2 mb-2">
                          <ExclamationTriangle size={14} className="me-1" />
                          <small>
                            Giờ leo thang phải nhỏ hơn giờ tự động duyệt
                          </small>
                        </Alert>
                      )}
                      <Alert variant="info" className="mb-0 py-2">
                        <small>
                          <strong>Luồng:</strong> Giải trình gửi → {appeal.remind_hours}h: nhắc
                          manager → {appeal.escalate_hours}h: leo thang admin → {appeal.auto_approve_hours}h
                          ({Math.round(appeal.auto_approve_hours / 24)} ngày): tự động miễn phạt
                        </small>
                      </Alert>
                    </Col>
                  </Row>
                </Card.Body>
                <Card.Footer className="d-flex justify-content-end gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setAppeal({ ...DEFAULTS.appeal })}
                    disabled={saving}
                  >
                    <ArrowCounterclockwise size={14} className="me-1" />
                    Mặc định
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveAppeal}
                    disabled={
                      saving ||
                      appeal.remind_hours >= appeal.escalate_hours ||
                      appeal.escalate_hours >= appeal.auto_approve_hours
                    }
                  >
                    {saving ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <Save size={14} className="me-1" />
                    )}
                    Lưu giải trình
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          </>
        )}
      </Row>
    </PageLayout>
  );
}
